import React, { PureComponent } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import MediaQuery from 'react-responsive';
import Waypoint from 'react-waypoint';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/fiverr_context';

import createGigStore from '../../store';
import { getPackageById, isSinglePackage, isTriplePackage } from '../../reducers/packages';

import scrollToComponent from '../../utils/scrollToComponent';
import { isTouchDevice, isTouchDeviceByContext } from '../../utils/isTouchDevice';
import reformatCollections from '../../utils/reformatCollections';
import ImpressionsFactory from '../../utils/impressions/factory';

import {
    ABOUT_SELLER,
    DESCRIPTION,
    FAQ,
    ORDER_DETAILS,
    OVERVIEW,
    PACKAGES_TABLE,
    RECOMMENDATIONS,
    REVIEWS
} from '../../utils/pageSections';
import {
    validateDescription,
    validateFaq,
    validateOrderDetails,
    validateOverview,
    validatePackagesTable,
    validateReviews,
    validateSellerCard
} from '../../utils/mainComponentsValidator';
import BiEvents from '../../utils/biEvents';
import GigCurrencyConverter from '../../utils/gigCurrencyConverter';
import { MP_SHOW_PAGE_NAME } from '../../utils/mixPanelEvents';
import { isGigApproved, isGigPaused } from '../../utils/gigStatus';
import { isFiverrChoice } from '../../utils/fiverrChoice';
import { addChoiceView, removeChoiceView } from '../../utils/fiverrChoice/choiceViewLocalStorageManager';
import { isLoggedInUser } from '../../utils/isLoggedInUser';
import Logger from '../../utils/logger';
import PathfinderWrapper from '../../utils/pathfinderWrapper';
import { BASIC_PACKAGE_ID } from '../../utils/packagesUtils/constants';
import { GIG_SUB_HEADER } from '../../utils/events';
import {
    GTM_EVENT_NAME,
    MEDIA_GRID_MD,
    SCROLL_DURATION,
    SCROLL_OFFSET,
    WAYPOINT_OFFSET
} from '../../utils/constants';
import isInPersonalizedContentExp from '../../utils/experiments/personalizedSection';

import SellerCard from '../SellerCard';
import StudioCard from '../StudioCard';
import Description from '../Description';
import Faq from '../Faq';
import Reviews from '../Reviews';
import Tags from '../Tags';
import PackagesTabs from '../PackagesTabs';
import PackagesTable from '../PackagesTable';
import OrderDetails from '../OrderDetails';
import StudioBox from '../StudioBox';
import TopNav from '../TopNav';
import ShareYourGig from '../ShareYourGig';
import Gallery from '../Gallery';
import Overview from '../Overview';
import Portfolio from '../Portfolio';
import OutOfOffice from '../OutOfOffice';
import SimilarGigs from '../SimilarGigs';
import RecommendedInSc from '../RecommendedInSc';
import OtherGigsBy from '../OtherGigsBy';
import RecentlyViewedGigs from '../RecentlyViewedGigsWrapper';
import Disclaimer from '../Disclaimer';
import MoreServices from '../MoreServices';
import PausedGig from '../PausedGig';
import ContactSeller from '../ContactSeller';
import ProVideo from '../ProVideo';
import ProBanner from '../ProBanner';
import ProCertificateBanner from '../ProCertificateBanner';
import ReviewsSnippet from '../ReviewsSnippet';
import UGCTranslateButton from '../UGCTranslateButton';
import Seo from '../Seo';

import './style.scss';

class GigPage extends PureComponent {

    constructor(props) {
        super(props);

        const {
            requestContext,
            topNav,
            general
        } = this.props;

        if (!general || !requestContext) {
            // If general params  or requestContext are missing throw an error and don't render the page
            throw Error('Missing general data or requestContext');
        }

        this.state = {
            collections: topNav ? reformatCollections(topNav.collections) : {},
            sectionKey: OVERVIEW,
            prevSectionKey: OVERVIEW,
            useWaypoints: true,
            hideRecommended: false,
            isTouch: isTouchDeviceByContext(requestContext) || false,
            showTranslateBtnInNav: false,
            shouldFetchCarousels: false
        };

        this.scrollToSection = this.scrollToSection.bind(this);
        this.updateCollections = this.updateCollections.bind(this);
        this.onLeaveWaypoint = this.onLeaveWaypoint.bind(this);
        this.onEnterWaypoint = this.onEnterWaypoint.bind(this);
        this.onEmptyRecommendedForYouGigs = this.onEmptyRecommendedForYouGigs.bind(this);
        this.onComparePackagesClick = this.onComparePackagesClick.bind(this);

        this.store = createGigStore(this.props);
        this.useStickyLayout = this.shouldUseStickyLayout();

        // used for unwrapping/rewrapping
        this.orphans = [];
        this.displayContents = true;

        const packagesCount = _.get(this.store.getState(), 'packages.packageList.length', 3);

        this.biEvents = new BiEvents({ requestContext, general, packagesCount });
        this.impressionsFactory = new ImpressionsFactory({ requestContext, general, packagesCount });
        this.logger = Logger.init(requestContext);
        this.pathfinderWrapper = new PathfinderWrapper(this.logger);
    }

    getChildContext() {
        const { requestContext, general, currency, rollouts, choiceEligibilities, locale } = this.props;

        return {
            biEvents: this.biEvents,
            impressionsFactory: this.impressionsFactory,
            requestContext,
            general,
            logger: this.logger,
            currencyConverter: new GigCurrencyConverter(currency),
            pathfinderWrapper: this.pathfinderWrapper,
            rollouts,
            choiceEligibilities,
            locale
        };
    }

    componentDidMount() {
        this.setDisplayContentsSupport();
        this.reportPageView();
        this.gtmReport();
        this.updateChoiceViewStorage();

        // eslint-disable-next-line
        this.setState({
            isTouch: isTouchDevice(),
            shouldFetchCarousels: true
        });

    }

    setDisplayContentsSupport() {
        if (window.CSS && window.CSS.supports) {
            this.displayContents = CSS.supports('display: contents');
        } else if (window.supportsCSS) { // Opera's old version of css detection
            this.displayContents = window.supportsCSS('display:contents');
        } else {
            this.displayContents = false;
        }
    }

    reportPageView() {
        this.biEvents.sendMixPanelEvent({ mpEventType: 'trackPageView', eventName: MP_SHOW_PAGE_NAME });
    }

    gtmReport() {
        if (!window.dataLayer) {
            return;
        }

        const { general } = this.props;
        const { gigId, gigTitle, isPro, categorySlug, subCategorySlug } = general;
        const { packages } = this.store.getState();
        const basicPackage = getPackageById(packages, BASIC_PACKAGE_ID);

        window.dataLayer.push({
            event: GTM_EVENT_NAME,
            gigId,
            gigTitle,
            isPro,
            categorySlug,
            subCategorySlug,
            basePrice: basicPackage ? basicPackage.price : 0
        });
    }

    updateChoiceViewStorage() {
        const { requestContext, general, choiceEligibilities } = this.props;
        const { current_user, params, ab_tests: abTests } = requestContext;
        const { gigId } = general;
        if (!isLoggedInUser(current_user)) {
            return;
        }

        isFiverrChoice(params, abTests, choiceEligibilities) ? addChoiceView(gigId) : removeChoiceView(gigId);
    }

    scrollToSection({ sectionKey }) {

        const refsObj = {};

        refsObj[OVERVIEW] = this.sectionOverview;
        refsObj[DESCRIPTION] = this.sectionDescription;
        refsObj[RECOMMENDATIONS] = this.sectionRecommendations;
        refsObj[FAQ] = this.sectionFaq;
        refsObj[REVIEWS] = this.sectionReviews;
        refsObj[ORDER_DETAILS] = this.sectionOrderDetails;
        refsObj[ABOUT_SELLER] = this.sectionAboutSeller;
        refsObj[PACKAGES_TABLE] = this.sectionPackagesTable;

        const currentSection = this.state.sectionKey;

        if ((refsObj[sectionKey]) && (sectionKey !== currentSection)) {

            this.setState({
                sectionKey,
                useWaypoints: false,
                prevSectionKey: currentSection
            }, () => {

                const scroller = scrollToComponent(refsObj[sectionKey], { offset: SCROLL_OFFSET, align: 'top', duration: SCROLL_DURATION });

                // we need a timeout here, otherwise the waypoints fire
                // right at the end of the scroll
                scroller.onComplete(() => {
                    setTimeout(() => {
                        this.setState({
                            useWaypoints: true
                        });
                    }, 50);
                });

            });

        }
    }

    updateCollections(collections) {
        this.setState({ collections });
    }

    onEnterWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;
        const scrollingUp = (data.currentPosition === 'inside') && (data.previousPosition === 'above');

        if ((useWaypoints) && (newSection !== currentSection) && (scrollingUp)) {
            this.setState({
                sectionKey: newSection,
                prevSectionKey: currentSection
            });
        }
    }

    onLeaveWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;

        if ((useWaypoints) && (newSection !== currentSection)) {

            if (data.waypointTop < WAYPOINT_OFFSET) {
                this.setState({
                    sectionKey: newSection,
                    prevSectionKey: currentSection
                });
            } else {
                this.setState({
                    sectionKey: currentSection
                });
            }
        }

    }

    isPro() {
        const { general } = this.props;

        return general.isPro;
    }

    isPageOwner() {
        const { requestContext } = this.props,
            { current_user, params } = requestContext;

        return isLoggedInUser(current_user) && (params.username === current_user.username);
    }

    getPageSectionsList() {
        const {
            general,
            overview,
            description,
            faq,
            reviews,
            sellerCard
        } = this.props;

        const { hideRecommended } = this.state;
        const { isStudio } = general;

        return [
            { sectionKey: OVERVIEW, validated: validateOverview({ overview }) },
            { sectionKey: DESCRIPTION, validated: validateDescription({ description }) },
            { sectionKey: FAQ, validated: validateFaq({ faq }) },
            { sectionKey: REVIEWS, validated: validateReviews({ reviews }) },
            { sectionKey: ABOUT_SELLER, validated: !isStudio && validateSellerCard({ sellerCard }) },
            { sectionKey: PACKAGES_TABLE, validated: this.shouldRenderPackagesTable() },
            { sectionKey: ORDER_DETAILS, validated: this.shouldRenderOrderDetails() },
            { sectionKey: RECOMMENDATIONS, validated: !hideRecommended && this.shouldRenderSimilarGigs() }
        ];
    }

    unwrapChildren() {

        if ((!this.sidebar && !this.main) || (this.displayContents)) {
            return;
        }

        this.orphans = [];
        while (this.sidebar.firstChild) {
            this.orphans.push(this.sidebar.firstChild);
            this.main.insertBefore(this.sidebar.firstChild, this.main.firstChild);
        }

    }

    rewrapChildren() {

        if ((!this.sidebar && !this.main) || (this.displayContents)) {
            return;
        }

        for (let i=0; i<this.orphans.length; i++) {
            this.sidebar.appendChild(this.orphans[i]);
        }

    }

    onEmptyRecommendedForYouGigs() {
        this.setState({
            hideRecommended: true
        });
    }

    onComparePackagesClick() {
        this.scrollToSection({ sectionKey: PACKAGES_TABLE });
    }

    shouldRenderPackagesTable() {
        const { packages } = this.store.getState();

        return validatePackagesTable({ isTriplePackage: isTriplePackage(packages) });
    }

    shouldRenderMoreServices() {
        const { general } = this.props;
        const { isStudio } = general;

        return !isStudio;
    }

    shouldRenderOutOfOffice() {
        const { general } = this.props;
        const { isOnVacation, isStudio } = general;

        return isOnVacation && !isStudio;
    }

    shouldRenderOrderDetails() {
        const { packages } = this.store.getState();

        return validateOrderDetails({ isSinglePackage: isSinglePackage(packages) });
    }

    shouldRenderSimilarGigs() {
        const { general } = this.props;
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        const isPro = this.isPro();

        return !isPro && !isPageOwner && isApproved && !isStudio;
    }

    shouldRenderRecommendedInSc() {
        const { general } = this.props;
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);

        return !isPageOwner && isApproved && !isStudio;
    }

    shouldRenderRecentlyGigs() {
        const isPageOwner = this.isPageOwner();
        const isPro = this.isPro();

        return !isPro && !isPageOwner;
    }

    shouldUseStickyLayout() {
        const { general } = this.props;

        return !general.isStudio;
    }

    render() {

        const {
            description,
            reviews,
            sellerCard,
            tags,
            topNav,
            gallery,
            overview,
            portfolio,
            customOrder,
            requestContext,
            outOfOffice,
            moreServices,
            otherGigs,
            general,
            studio,
            gigReport,
            rollouts,
            seo,
            faq
        } = this.props;

        const {
            collections,
            sectionKey,
            isTouch,
            showTranslateBtnInNav,
            shouldFetchCarousels
        } = this.state;

        const { gigStatus, traffiqed, isStudio, gigId } = general;
        const { params, current_user, ab_tests: abTests } = requestContext;
        const { username, slug } = params;
        const { info, services } = studio;
        const { allowUGCTranslation } = rollouts;
        const { metadataAttributes: descriptionMetadata } = description;

        const isLoggedIn = isLoggedInUser(current_user);
        const isPageOwner = this.isPageOwner();
        const isPaused = isGigPaused(gigStatus, traffiqed);
        const isPro = this.isPro();

        const shouldRenderPackagesTable = this.shouldRenderPackagesTable();
        const shouldRenderMoreServices = this.shouldRenderMoreServices();
        const shouldRenderOutOfOffice = this.shouldRenderOutOfOffice();

        const outerClasses = classNames('gig-page-wrapper responsive', { 'pro-gig': isPro, 'touch': isTouch, 'sidebar-sticky': this.useStickyLayout });
        const sidebarClasses = classNames('sidebar',
            { 'poly-sticky': this.useStickyLayout });

        return (
            <Provider store={this.store}>
                {/* TODO remove the data attributes after refactoring first-message-popup into a  v4 package */}
                {/* The reason to the data attributes is that the first-message-popup in v2 requires this data
                    and the initailData is no longer avaialable in v2 routes */}
                <div className={outerClasses} data-gig-id={gigId} data-username={username} data-slug={slug}>
                    <Seo seo={seo} faq={faq}/>
                    <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                        {(matches) => {
                            if (matches) {
                                this.rewrapChildren();
                            } else {
                                this.unwrapChildren();
                            }
                            return false;
                        }}
                    </MediaQuery>

                    <TopNav topNav={topNav}
                        showUGCTranslateBtn={showTranslateBtnInNav && allowUGCTranslation}
                        pageSectionsList={this.getPageSectionsList()}
                        collections={collections}
                        scrollToSection={this.scrollToSection}
                        isLoggedIn={isLoggedIn}
                        isPageOwner={isPageOwner}
                        isTrustedUser={gigReport.isTrustedUser}
                        selSection={sectionKey}/>

                    <div className="gig-page-top">
                        {isPageOwner && <ShareYourGig/>}
                        {!isPageOwner && isPaused && <PausedGig/>}
                    </div>

                    <div className="gig-page">

                        <aside className={sidebarClasses} ref={(ele) => { this.sidebar = ele; }}>

                            {isStudio && <StudioBox/>}

                            <PackagesTabs customOrder={customOrder}
                                onComparePackagesClick={this.onComparePackagesClick}/>

                            {this.useStickyLayout && <ContactSeller customOrder={customOrder} isPaused={isPaused}/>}
                            {!this.useStickyLayout && <StudioCard studio={studio}/>}

                        </aside>

                        <div className="main" ref={(ele) => { this.main = ele; }}>

                            {isPro && <ProBanner/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(OVERVIEW, data))}
                                onLeave={((data) => this.onLeaveWaypoint(OVERVIEW, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            {shouldRenderOutOfOffice &&
                                <OutOfOffice outOfOffice={outOfOffice} isLoggedIn={isLoggedIn} isPageOwner={isPageOwner}/>
                            }
                            <Overview overview={overview} studioInfo={info} ref={(ele) => { this.sectionOverview = ele; }}
                                scrollToSection={this.scrollToSection} useStickyLayout={this.useStickyLayout}/>

                            <Waypoint onEnter={() => this.setState({ showTranslateBtnInNav: false })}
                                onLeave={() => this.setState({ showTranslateBtnInNav: true })}
                                topOffset={WAYPOINT_OFFSET}/>

                            {allowUGCTranslation &&
                            <UGCTranslateButton componentName={GIG_SUB_HEADER}
                                gigId={gigId} user={current_user}/>}

                            <Gallery gallery={gallery} isTouch={isTouch}/>
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(DESCRIPTION, data))}
                                onLeave={((data) => this.onLeaveWaypoint(DESCRIPTION, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            <ReviewsSnippet reviews={reviews} scrollToSection={this.scrollToSection}/>
                            <Description metadataAttributes={descriptionMetadata}
                                ref={(ele) => { this.sectionDescription = ele; }}/>

                            {!isStudio && <Disclaimer/>}

                            {isPro && <ProCertificateBanner/>}

                            {!isStudio &&
                                <>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(ABOUT_SELLER, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(ABOUT_SELLER, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <h2 className="section-title about-the-seller" ref={(ele) => { this.sectionAboutSeller = ele; }}>
                                        <I18n k={'gig_page_perseus.general.about_the_seller'}/>
                                    </h2>
                                    <SellerCard {...sellerCard}/>
                                    <Portfolio portfolio={portfolio} isPageOwner={isPageOwner}/>
                                </>
                            }

                            {shouldRenderPackagesTable &&
                                <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(PACKAGES_TABLE, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(PACKAGES_TABLE, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <PackagesTable services={services} ref={(ele) => {this.sectionPackagesTable = ele;}}/>
                                </MediaQuery>
                            }

                            {this.shouldRenderOrderDetails() &&
                            <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                <Waypoint onEnter={((data) => this.onEnterWaypoint(ORDER_DETAILS, data))} onLeave={((data) => this.onLeaveWaypoint(ORDER_DETAILS, data))} topOffset={WAYPOINT_OFFSET}/>
                                <OrderDetails ref={(ele) => { this.sectionOrderDetails = ele; }}/>
                            </MediaQuery>
                            }

                            <Waypoint onEnter={((data) => this.onEnterWaypoint(RECOMMENDATIONS, data))}
                                onLeave={((data) => this.onLeaveWaypoint(RECOMMENDATIONS, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            {this.shouldRenderSimilarGigs() &&
                                <SimilarGigs ref={(ele) => { this.sectionRecommendations = ele; }}
                                    collections={collections}
                                    isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    updateCollections={this.updateCollections}
                                    onEmptyGigs={this.onEmptyRecommendedForYouGigs}
                                    shouldFetchCarousel={shouldFetchCarousels}/>
                            }

                            {!isStudio && shouldRenderMoreServices &&
                                <MoreServices {...moreServices} sellerName={params.username}/>
                            }
                            {isPro && <ProVideo/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(FAQ, data))} onLeave={((data) => this.onLeaveWaypoint(FAQ, data))} topOffset={WAYPOINT_OFFSET}/>
                            <Faq ref={(ele) => { this.sectionFaq = ele; }}/>

                            <Waypoint onEnter={((data) => this.onEnterWaypoint(REVIEWS, data))} onLeave={((data) => this.onLeaveWaypoint(REVIEWS, data))} topOffset={WAYPOINT_OFFSET}/>
                            <Reviews reviews={reviews} ref={(ele) => { this.sectionReviews = ele; }}/>

                            <Tags tags={tags}/>
                        </div>
                    </div>

                    <aside className="gig-page-bottom">
                        <div className="max-width-container">

                            <OtherGigsBy otherGigs={otherGigs}
                                collections={collections}
                                updateCollections={this.updateCollections}
                                isLoggedIn={isLoggedIn}
                                isTouch={isTouch}
                                studioInfo={info}
                                shouldFetchCarousel={shouldFetchCarousels}/>

                            {this.shouldRenderRecommendedInSc() && !isInPersonalizedContentExp(abTests) &&
                            <RecommendedInSc collections={collections}
                                updateCollections={this.updateCollections}
                                isLoggedIn={isLoggedIn}
                                isTouch={isTouch}
                                shouldFetchCarousel={shouldFetchCarousels}/>
                            }

                            {/* todo PersonalizedSection uses pathfinder in a way that the make the build fail
                                uncomment this code once the issue is resolved*/}
                            {/* {this.shouldRenderRecommendedInSc() && isInPersonalizedContentExp(abTests) &&
                            <PersonalizedSectionWrapper personalizedContent={personalizedContent}/>
                            } */}

                            {this.shouldRenderRecentlyGigs() &&
                            <RecentlyViewedGigs collections={collections}
                                updateCollections={this.updateCollections}
                                isLoggedIn={isLoggedIn}
                                isTouch={isTouch}
                                shouldFetchCarousel={shouldFetchCarousels}/>
                            }

                        </div>
                    </aside>

                </div>
            </Provider>
        );
    }
}

GigPage.defaultProps = {
    sellerCard: {},
    studio: {},
    gigReport: {},
    rollouts: {},
    description: {}
};

GigPage.childContextTypes = {
    biEvents: PropTypes.object,
    impressionsFactory: PropTypes.object,
    logger: PropTypes.object,
    locale: PropTypes.string,
    general: PropTypes.object,
    requestContext: PropTypes.object,
    currencyConverter: PropTypes.object,
    pathfinderWrapper: PropTypes.object,
    rollouts: PropTypes.object,
    choiceEligibilities: PropTypes.array
};

export default GigPage;
